import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  CheckCircleIcon,
  CreditCardIcon,
  DocumentTextIcon,
  CalendarIcon,
  EnvelopeIcon,
  PhoneIcon,
  PrinterIcon,
  ArrowRightIcon,
  StarIcon,
  ShieldCheckIcon,
  SparklesIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [confetti, setConfetti] = useState(true);
  const [bookingCreated, setBookingCreated] = useState(false);
  const [createdBookingCode, setCreatedBookingCode] = useState<string | null>(null);
  const bookingAttempted = useRef(false);

  // Extract parameters from URL - handle multiple parameters with same name (Flywire appends its own)
  const getAllParams = (key: string) => searchParams.getAll(key);
  const getParam = (key: string) => {
    const vals = getAllParams(key);
    return vals.length > 0 ? vals[vals.length - 1] : null; // Get the last one (Flywire's appended value)
  };

  const urlReference = getParam('reference');
  const urlTransactionId = getParam('transactionId');
  const urlPayment = getParam('payment');
  const urlBooking = getParam('booking');
  
  // Use the Flywire reference if available, otherwise fallback to our generated TXN ID
  const transactionId = urlReference || urlTransactionId || urlPayment;
  const bookingCode = urlBooking || urlReference;
  
  const amount = getParam('amount');
  const currency = getParam('currency');
  const packageName = getParam('package') || 'Custom Payment';
  const customerName = getParam('name') || 'Valued Customer';
  const email = getParam('email');
  const paymentMethodParam = getParam('paymentMethod') || (transactionId?.startsWith('85') ? 'PayPal' : 'Flywire');

  const [bookingStatus, setBookingStatus] = useState<'idle' | 'creating' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Determine if this is a manual payment or package payment
  const isManualPayment = packageName === 'Custom Payment';
  const isPackagePayment = !isManualPayment;

  // Create booking on page load (for Flywire redirect-based flow)
  useEffect(() => {
    const createBookingOnLoad = async () => {
      // Prevent duplicate attempts
      if (bookingAttempted.current) return;
      
      console.log('🔍 Checking if booking needs to be created...', {
        transactionId,
        amount,
        currency,
        email,
        paymentMethodParam
      });

      // Only create booking if we have the necessary data
      if (!transactionId || !amount || !currency || !email) {
        console.warn('⚠️ Missing required data for booking creation. URL Params:', Object.fromEntries(searchParams.entries()));
        return;
      }

      // Skip if this looks like a PayPal payment (PayPal creates booking in capture-order)
      if (paymentMethodParam === 'PayPal' || (urlTransactionId && urlTransactionId.startsWith('85'))) {
        console.log('✅ PayPal payment detected - booking should already exist');
        setBookingCreated(true);
        setBookingStatus('success');
        return;
      }

      bookingAttempted.current = true;
      setBookingStatus('creating');

      try {
        console.log('🔄 Initiating booking creation from success page redirect...');
        
        // Parse customer name
        const nameParts = customerName.split(' ');
        const firstName = nameParts[0] || 'Guest';
        const lastName = nameParts.slice(1).join(' ') || 'Customer';
        const packageId = searchParams.get('packageId');
        const phone = searchParams.get('phone') || '';

        // Determine correct endpoint
        // A valid MongoDB ObjectId is 24 characters hex
        const isValidObjectId = packageId && /^[0-9a-fA-F]{24}$/.test(packageId);
        const endpoint = isValidObjectId ? '/api/bookings/create' : '/api/bookings/manual';
        
        const bookingData: any = {
          firstName,
          lastName,
          email,
          phone: phone || '0000000000',
          amount: parseFloat(amount),
          currency,
          packageName,
          transactionId,
          paymentMethod: 'flywire'
        };

        if (isValidObjectId) {
          bookingData.packageId = packageId;
        }

        console.log(`📤 Sending request to ${endpoint}:`, bookingData);
        
        const response = await axios.post(endpoint, bookingData);

        if (response.data.success) {
          console.log('✅ Booking created successfully:', response.data);
          setBookingCreated(true);
          setBookingStatus('success');
          setCreatedBookingCode(response.data.booking?.bookingCode);
        } else {
          console.warn('⚠️ Backend returned non-success response:', response.data);
          if (response.data.message?.includes('already exists')) {
            setBookingCreated(true);
            setBookingStatus('success');
          } else {
            setBookingStatus('error');
            setErrorMessage(response.data.message || 'Unknown error from server');
          }
        }
      } catch (error: any) {
        console.error('❌ Failed to create booking:', error);
        
        if (error.response) {
          console.error('Error Response Data:', error.response.data);
          
          if (error.response.data?.message?.includes('already exists') || error.response.status === 409) {
            console.log('ℹ️ Booking already exists (idempotency caught)');
            setBookingCreated(true);
            setBookingStatus('success');
          } else {
            setBookingStatus('error');
            setErrorMessage(error.response.data?.message || 'Server error occurred');
          }
        } else {
          setBookingStatus('error');
          setErrorMessage('Network error - please check your connection');
        }
      }
    };

    createBookingOnLoad();
  }, [transactionId, amount, currency, email, customerName, packageName, paymentMethodParam, searchParams, urlTransactionId]);

  useEffect(() => {
    // Hide confetti after animation
    const timer = setTimeout(() => setConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Helmet>
        <title>{isManualPayment ? 'Payment Received' : 'Booking Confirmed'} - Thank You! | Bali Getaway</title>
        <meta name="description" content={isManualPayment ? 'Your payment has been received successfully. We will contact you shortly.' : 'Your booking has been confirmed! Welcome to your Bali adventure!'} />
      </Helmet>

      {/* Confetti Animation */}
      {confetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <div className="confetti-animation">
            {Array.from({ length: 50 }).map((_, i) => (
              <div
                key={i}
                className="confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  backgroundColor: ['#f59e0b', '#ea580c', '#dc2626', '#7c3aed', '#2563eb'][Math.floor(Math.random() * 5)]
                }}
              />
            ))}
          </div>
        </div>
      )}

      <style>{`
        .confetti-animation {
          position: relative;
          width: 100%;
          height: 100%;
        }
        .confetti-piece {
          position: absolute;
          width: 10px;
          height: 10px;
          animation: confetti-fall 3s linear forwards;
        }
        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 px-4 py-16">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-4xl mx-auto"
        >
          {/* Success Header - Conditional */}
          <div className="text-center mb-16">
            <div className="relative inline-block">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
                className={`w-32 h-32 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-2xl ${isManualPayment
                  ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                  : 'bg-gradient-to-br from-green-500 to-emerald-600'
                }`}
              >
                <CheckCircleIcon className="w-16 h-16 text-white" />
              </motion.div>
              <motion.div 
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ repeat: Infinity, duration: 4 }}
                className="absolute -top-4 -right-4"
              >
                <StarIcon className="w-12 h-12 text-yellow-400 fill-current drop-shadow-md" />
              </motion.div>
            </div>

            <h1 className="text-5xl lg:text-7xl font-black text-gray-900 mb-6 tracking-tight">
              {isManualPayment ? 'Payment Received!' : 'Booking Confirmed!'}
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              {isManualPayment
                ? `Thank you ${customerName}! We've received your payment and our team will get in touch shortly.`
                : `Get ready for your dream Bali adventure, ${customerName}! Your voucher is ready.`
              }
            </p>
            <div className={`inline-flex items-center px-8 py-3 rounded-2xl text-lg font-bold shadow-sm ${isManualPayment
                ? 'bg-blue-100 text-blue-800 border border-blue-200'
                : 'bg-green-100 text-green-800 border border-green-200'
              }`}>
              <ShieldCheckIcon className="w-6 h-6 mr-3 text-current" />
              Verified & Secured
            </div>

            {/* Booking Creation Status Indicator */}
            <div className="mt-8 max-w-md mx-auto">
              <AnimatePresence mode="wait">
                {bookingStatus === 'creating' && (
                  <motion.div
                    key="creating"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-orange-100 flex items-center justify-center gap-3"
                  >
                    <div className="w-5 h-5 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-orange-700 font-bold">Finalizing your booking...</span>
                  </motion.div>
                )}

                {bookingStatus === 'success' && (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-green-50 rounded-2xl p-4 shadow-sm border border-green-100 flex items-center justify-center gap-3"
                  >
                    <CheckIcon className="w-6 h-6 text-green-600" />
                    <span className="text-green-800 font-bold">Booking secured & Email sent!</span>
                  </motion.div>
                )}

                {bookingStatus === 'error' && (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-red-50 rounded-2xl p-4 shadow-sm border border-red-100 flex flex-col items-center justify-center gap-2"
                  >
                    <div className="flex items-center gap-3">
                      <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                      <span className="text-red-800 font-bold">Booking Creation Issue</span>
                    </div>
                    {errorMessage && <p className="text-red-600 text-sm">{errorMessage}</p>}
                    <button 
                      onClick={() => {
                        bookingAttempted.current = false;
                        setBookingStatus('idle');
                        window.location.reload();
                      }}
                      className="mt-2 text-xs bg-red-600 text-white px-3 py-1 rounded-full font-bold hover:bg-red-700 transition-colors"
                    >
                      Try Again
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Conditional Content Based on Payment Type */}
          {isManualPayment ? (
            /* Manual Payment Card */
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-gray-100 mb-12"
            >
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-10 flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-black mb-2">Transaction Receipt</h2>
                  <p className="text-blue-100 text-lg font-medium opacity-90">Payment processed successfully</p>
                </div>
                <div className="hidden md:block">
                  <CreditCardIcon className="w-16 h-16 text-white/20" />
                </div>
              </div>

              <div className="p-10">
                <div className="max-w-2xl mx-auto space-y-6">
                  {/* Transaction Information */}
                  <div className="bg-gray-50 rounded-3xl p-8 space-y-4 border border-gray-100">
                    {amount && currency && (
                      <div className="flex justify-between items-center pb-4 border-b border-gray-200/60">
                        <span className="text-gray-500 font-bold uppercase tracking-wider text-xs">Amount Paid</span>
                        <span className="text-3xl font-black text-gray-900">{currency} {parseFloat(amount).toFixed(2)}</span>
                      </div>
                    )}
                    {transactionId && (
                      <div className="flex justify-between items-center py-4 border-b border-gray-200/60">
                        <span className="text-gray-500 font-bold uppercase tracking-wider text-xs">Transaction ID</span>
                        <span className="font-mono font-bold text-gray-900 bg-white px-3 py-1 rounded-lg border border-gray-100 shadow-sm text-sm">{transactionId}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-4 border-b border-gray-200/60">
                      <span className="text-gray-500 font-bold uppercase tracking-wider text-xs">Payment Date</span>
                      <span className="font-bold text-gray-900">{new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                    <div className="flex justify-between items-center py-4 border-b border-gray-200/60">
                      <span className="text-gray-500 font-bold uppercase tracking-wider text-xs">Method</span>
                      <div className="flex items-center gap-2 font-bold text-gray-900">
                        <span className="text-xl">{paymentMethodParam === 'PayPal' ? '💳' : '🌐'}</span>
                        {paymentMethodParam}
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-4">
                      <span className="text-gray-500 font-bold uppercase tracking-wider text-xs">Status</span>
                      <span className="inline-flex items-center px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-black uppercase tracking-tighter border border-green-200">
                        <CheckCircleIcon className="w-4 h-4 mr-1.5" />
                        Completed
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center px-4 text-gray-500 font-medium">
                    <span>Customer Name</span>
                    <span className="font-bold text-gray-900">{customerName}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            /* Package Payment Card */
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-gray-100 mb-12"
            >
              <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white p-10 flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-black mb-2">Booking Confirmation</h2>
                  <p className="text-green-100 text-lg font-medium opacity-90">Your voucher is now active</p>
                </div>
                <div className="hidden md:block">
                  <SparklesIcon className="w-16 h-16 text-white/20" />
                </div>
              </div>

              <div className="p-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  {/* Payment Information */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-black text-gray-900 flex items-center uppercase tracking-tight">
                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center mr-3">
                        <CreditCardIcon className="w-5 h-5 text-green-600" />
                      </div>
                      Payment Details
                    </h3>
                    <div className="bg-gray-50 rounded-3xl p-6 space-y-4 border border-gray-100">
                      {amount && currency && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Amount Paid</span>
                          <span className="font-black text-gray-900 text-xl">{currency} {parseFloat(amount).toFixed(2)}</span>
                        </div>
                      )}
                      {bookingCode && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Booking Code</span>
                          <span className="font-mono text-lg font-black text-green-600 bg-green-50 px-3 py-1 rounded-xl border border-green-100">{bookingCode}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Date</span>
                        <span className="font-bold text-gray-900">{new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Status</span>
                        <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-black uppercase tracking-tighter border border-green-200">
                          Confirmed
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Package Information */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-black text-gray-900 flex items-center uppercase tracking-tight">
                      <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center mr-3">
                        <DocumentTextIcon className="w-5 h-5 text-orange-600" />
                      </div>
                      Package Summary
                    </h3>
                    <div className="bg-gray-50 rounded-3xl p-6 space-y-4 border border-gray-100">
                      <div>
                        <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Selected Plan</span>
                        <div className="font-black text-gray-900 mt-1 leading-tight">{packageName}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm">
                          <span className="text-gray-400 font-bold text-[10px] uppercase block mb-1">Validity</span>
                          <span className="font-bold text-gray-900 text-sm">12 Months</span>
                        </div>
                        <div className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm">
                          <span className="text-gray-400 font-bold text-[10px] uppercase block mb-1">Guests</span>
                          <span className="font-bold text-gray-900 text-sm">Up to 2 Pers</span>
                        </div>
                      </div>
                      <div className="pt-2">
                        <span className="text-gray-500 font-bold text-xs uppercase tracking-wider block mb-1">Voucher Expires</span>
                        <span className="font-bold text-gray-900 text-sm">{new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* What's Next */}
          <div className="bg-white rounded-[3rem] shadow-xl p-12 mb-12 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gray-50 rounded-full -mr-32 -mt-32 opacity-50"></div>
            <h2 className="text-3xl font-black text-gray-900 mb-10 text-center relative">What Happens Next?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
              <motion.div 
                whileHover={{ y: -5 }}
                className="text-center group"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-200 group-hover:scale-110 transition-transform">
                  <EnvelopeIcon className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-black text-gray-900 mb-3 text-lg">Email Confirmation</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">You'll receive a detailed confirmation with your receipt within 15 minutes.</p>
              </motion.div>

              <motion.div 
                whileHover={{ y: -5 }}
                className="text-center group"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-200 group-hover:scale-110 transition-transform">
                  <PhoneIcon className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-black text-gray-900 mb-3 text-lg">Travel Consultation</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">Our travel experts will contact you within 24 hours to help plan your perfect stay.</p>
              </motion.div>

              <motion.div 
                whileHover={{ y: -5 }}
                className="text-center group"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-200 group-hover:scale-110 transition-transform">
                  <CalendarIcon className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-black text-gray-900 mb-3 text-lg">Pick Your Dates</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">Schedule your Bali getaway anytime over the next 12 months at your convenience.</p>
              </motion.div>
            </div>
          </div>

          {/* Important Information */}
          <div className="bg-gradient-to-br from-orange-500 to-yellow-50 rounded-[3rem] p-12 mb-12 text-white shadow-2xl shadow-orange-200 relative overflow-hidden group">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent"></div>
            <h3 className="text-2xl font-black mb-8 text-center flex items-center justify-center gap-3">
              <SparklesIcon className="w-8 h-8" />
              Important Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 relative">
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20">
                <h4 className="font-black mb-4 text-lg uppercase tracking-tight">Voucher Validity</h4>
                <ul className="text-sm space-y-3 font-bold opacity-90">
                  <li className="flex items-start gap-2">
                    <CheckIcon className="w-5 h-5 flex-shrink-0" />
                    Valid for 12 months from today
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="w-5 h-5 flex-shrink-0" />
                    Flexible booking with 30-day notice
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="w-5 h-5 flex-shrink-0" />
                    Voucher is fully transferable
                  </li>
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20">
                <h4 className="font-black mb-4 text-lg uppercase tracking-tight">Need Assistance?</h4>
                <div className="space-y-4">
                  <a href="tel:+610212345678" className="flex items-center gap-4 bg-white/20 p-3 rounded-2xl hover:bg-white/30 transition-colors">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                      <PhoneIcon className="w-5 h-5 text-orange-600" />
                    </div>
                    <span className="font-black tracking-tight">+61 (02) 1234 5678</span>
                  </a>
                  <a href="mailto:bookings@baligetaway.com" className="flex items-center gap-4 bg-white/20 p-3 rounded-2xl hover:bg-white/30 transition-colors">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                      <EnvelopeIcon className="w-5 h-5 text-orange-600" />
                    </div>
                    <span className="font-black tracking-tight text-sm truncate">bookings@baligetaway.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePrint}
              className="inline-flex items-center justify-center px-10 py-5 bg-white text-gray-900 font-black rounded-[2rem] hover:bg-gray-50 transition-all shadow-xl border border-gray-100"
            >
              <PrinterIcon className="mr-3 w-6 h-6 text-gray-400" />
              PRINT RECEIPT
            </motion.button>

            {bookingCode && (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to={`/booking-confirmation/${bookingCode}`}
                  className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-black rounded-[2rem] transition-all shadow-2xl shadow-blue-200"
                >
                  <DocumentTextIcon className="mr-3 w-6 h-6" />
                  VIEW VOUCHER
                </Link>
              </motion.div>
            )}

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/packages"
                className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-black rounded-[2rem] transition-all shadow-2xl shadow-orange-200"
              >
                <SparklesIcon className="mr-3 w-6 h-6" />
                EXPLORE BALI
              </Link>
            </motion.div>
          </div>

          {/* Social Sharing */}
          <div className="text-center pt-8 border-t border-gray-200/60">
            <p className="text-gray-400 font-black uppercase tracking-widest text-xs mb-6">Share your dream trip</p>
            <div className="flex justify-center gap-6">
              {['📘', '📸', '🐦'].map((emoji, idx) => (
                <motion.button 
                  key={idx}
                  whileHover={{ scale: 1.2, rotate: [0, 10, -10, 0] }}
                  className="w-14 h-14 bg-white rounded-2xl shadow-lg border border-gray-100 flex items-center justify-center text-3xl"
                >
                  {emoji}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default PaymentSuccessPage;
